import type { EventSource } from "./eventDetails";
import type { Message } from "../data/demo";
import type { ChatNoticeVariant } from "../components/ChatNotice.vue";

export const DEMO_DATE = "2026-09-20";
export interface ConversationNotice {
  id: string;
  text: string;
  variant?: Exclude<ChatNoticeVariant, "date" | "unread">;
  date?: string;
  beforeMessageId?: string;
  /** When it happened: placed among the messages by time (the conversation's events). */
  ts?: number;
  /** The event behind the notice, for its "Dados do evento". */
  source?: EventSource;
  icon?: string;
  actionLabel?: string;
  action?: string;
}
export type TimelineEntry =
  | { id: string; kind: "date"; date: string; label: string }
  | { id: string; kind: "message"; message: Message }
  | { id: string; kind: "album"; messages: Message[] }
  | { id: string; kind: "notice"; notice: ConversationNotice }
  | { id: string; kind: "unread"; count: number };

/** WhatsApp groups four or more photos or videos sent together, without captions. */
export const ALBUM_MIN = 4;
/** Longest pause between two items of the same album. */
export const ALBUM_GAP_MS = 2 * 60 * 1000;

function albumItem(m: Message): boolean {
  return (
    (m.kind === "image" || m.kind === "video") &&
    !m.text &&
    !m.deleted &&
    !m.reply &&
    !m.failed
  );
}

function sameBurst(a: Message, b: Message): boolean {
  if (a.out !== b.out || a.date !== b.date) return false;
  if (a.ts !== undefined && b.ts !== undefined)
    return Math.abs(b.ts - a.ts) <= ALBUM_GAP_MS;
  return a.time === b.time;
}

/** Merges runs of consecutive album-eligible messages into album entries. */
export function withAlbums(entries: TimelineEntry[]): TimelineEntry[] {
  const out: TimelineEntry[] = [];
  let run: Message[] = [];
  const flush = () => {
    if (run.length >= ALBUM_MIN)
      out.push({ id: `album-${run[0]!.id}`, kind: "album", messages: run });
    else
      for (const message of run)
        out.push({ id: `message-${message.id}`, kind: "message", message });
    run = [];
  };
  for (const entry of entries) {
    if (entry.kind === "message" && albumItem(entry.message)) {
      const last = run.at(-1);
      if (last && !sameBurst(last, entry.message)) flush();
      run.push(entry.message);
      continue;
    }
    flush();
    out.push(entry);
  }
  flush();
  return out;
}

function side(entry: TimelineEntry | undefined): boolean | null {
  if (entry?.kind === "message") return entry.message.out;
  if (entry?.kind === "album") return entry.messages[0]?.out ?? null;
  return null;
}

/**
 * WhatsApp draws the bubble tail only on the first message of a run from the same side;
 * the messages that follow sit 2 px apart without it. A date, notice or unread marker starts a new run.
 */
export function startsRun(entries: TimelineEntry[], index: number): boolean {
  const current = side(entries[index]);
  return current === null || side(entries[index - 1]) !== current;
}

export function localDate(date = new Date()): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
export function dateLabel(date: string, now = new Date()): string {
  if (date === localDate(now)) return "Hoje";
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date === localDate(yesterday)) return "Ontem";
  const [year, month, day] = date.split("-");
  return `${day}/${month}/${year}`;
}
export function buildTimeline(
  messages: Message[],
  notices: ConversationNotice[] = [],
  unread?: { id: string; count: number } | null,
  now = new Date(),
): TimelineEntry[] {
  const entries: TimelineEntry[] = [];
  let currentDate = "";
  function day(date: string) {
    if (date === currentDate) return;
    entries.push({
      id: `date-${date}-${entries.length}`,
      kind: "date",
      date,
      label: dateLabel(date, now),
    });
    currentDate = date;
  }
  function noticeRow(notice: ConversationNotice, fallback: string) {
    day(notice.date || fallback);
    entries.push({ id: `notice-${notice.id}`, kind: "notice", notice });
  }
  for (const notice of notices.filter(
    (n) => !n.beforeMessageId && n.ts === undefined,
  ))
    noticeRow(notice, messages[0]?.date || DEMO_DATE);
  // Events with a time sit between the messages around them; the latest go after the last one.
  const timed = notices
    .filter((n) => !n.beforeMessageId && n.ts !== undefined)
    .sort((a, b) => a.ts! - b.ts!);
  let next = 0;
  for (const message of messages) {
    while (next < timed.length && timed[next]!.ts! <= (message.ts ?? Infinity))
      noticeRow(timed[next++]!, message.date || DEMO_DATE);
    for (const notice of notices.filter(
      (n) => n.beforeMessageId === message.id,
    ))
      noticeRow(notice, message.date || DEMO_DATE);
    day(message.date || DEMO_DATE);
    if (unread?.id === message.id && unread.count > 0)
      entries.push({
        id: `unread-${message.id}`,
        kind: "unread",
        count: unread.count,
      });
    entries.push({ id: `message-${message.id}`, kind: "message", message });
  }
  while (next < timed.length)
    noticeRow(timed[next++]!, messages.at(-1)?.date || DEMO_DATE);
  return entries;
}

export interface DayGroup {
  key: string;
  date: Extract<TimelineEntry, { kind: "date" }> | null;
  /** The day's rows with their place in the whole timeline (tails look at the row before). */
  items: Array<{ entry: TimelineEntry; index: number }>;
}
/**
 * The timeline cut by day, so each day's date can stay at the top while its messages scroll
 * by, as on WhatsApp, until the next day's takes over.
 */
export function byDay(entries: TimelineEntry[]): DayGroup[] {
  const groups: DayGroup[] = [];
  entries.forEach((entry, index) => {
    if (entry.kind === "date") {
      groups.push({ key: entry.id, date: entry, items: [] });
      return;
    }
    if (!groups.length) groups.push({ key: "start", date: null, items: [] });
    groups.at(-1)!.items.push({ entry, index });
  });
  return groups;
}
